import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TextField, Button, Typography, Box, Alert, InputAdornment, IconButton } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import loginBg from '../assets/login-bg.webp.jpg';

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M1 10s3-6 9-6 9 6 9 6-3 6-9 6-9-6-9-6z" strokeLinecap="round"/>
    <circle cx="10" cy="10" r="2.5"/>
  </svg>
);
const EyeOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6">
    <path d="M3 3l14 14M8.5 8.6A2.5 2.5 0 0013.4 13M6.3 5.3C3.6 6.8 2 10 2 10s3 6 8 6c1.6 0 3-.5 4.2-1.3M10 4c5 0 8 6 8 6s-.8 1.5-2.3 3" strokeLinecap="round"/>
  </svg>
);

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err.message || err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      bgcolor: 'rgba(255,255,255,0.07)',
      borderRadius: '10px',
      color: '#fff',
      fontSize: '0.9rem',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.38)' },
      '&.Mui-focused fieldset': { borderColor: 'rgba(0,174,239,0.75)', borderWidth: '1.5px' },
      '& input': { color: '#fff' },
      '& input::placeholder': { color: 'rgba(255,255,255,0.32)', opacity: 1 },
    },
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      fontFamily: "'Inter', sans-serif",
      position: 'relative',
      pr: { xs: 0, md: '6%' },
      backgroundImage: `url(${loginBg})`,
      backgroundSize: '100%',
      backgroundPosition: 'left',
      backgroundRepeat: 'no-repeat',
      '&::before': {
        content: '""',
        position: 'absolute', inset: 0,
        background: 'linear-gradient(to right, rgba(2,12,40,0.3) 0%, rgba(2,12,40,0.55) 50%, rgba(2,12,40,0.75) 100%)',
        zIndex: 0,
      },
    }}>

      {/* Glassmorphism login card */}
      <Box sx={{
        position: 'relative', zIndex: 1,
        width: '100%', maxWidth: 420,
        mx: { xs: 'auto', md: 0 },
        px: { xs: 2, sm: 0 },
      }}>
        <Box sx={{
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          bgcolor: 'rgba(5, 18, 55, 0.72)',
          border: '1px solid rgba(255,255,255,0.13)',
          borderRadius: '20px',
          p: { xs: '28px 24px', sm: '40px 44px' },
          boxShadow: '0 8px 48px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08)',
        }}>

          {/* Logo */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3.5 }}>
            <img src="/shellkode_logo_white.svg" alt="ShellKode" style={{ height: 28 }} />
          </Box>

          <Typography sx={{
            fontSize: '1.5rem', fontWeight: 700, color: '#ffffff',
            fontFamily: "'Inter', sans-serif", textAlign: 'center',
            letterSpacing: '-0.02em', mb: 0.5,
          }}>
            Welcome back
          </Typography>
          <Typography sx={{
            fontSize: '0.875rem', color: 'rgba(255,255,255,0.48)',
            fontFamily: "'Inter', sans-serif", textAlign: 'center', mb: 3.5,
          }}>
            Sign in to your ShellKode account
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2.5, borderRadius: '10px', fontSize: '0.875rem',
                bgcolor: 'rgba(239,68,68,0.15)', color: '#fca5a5',
                border: '1px solid rgba(239,68,68,0.3)',
                '& .MuiAlert-icon': { color: '#fca5a5' },
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'rgba(255,255,255,0.75)', fontFamily: "'Inter', sans-serif", mb: 0.75 }}>
              Email address
            </Typography>
            <TextField
              required fullWidth size="small"
              id="email" autoComplete="email" autoFocus
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ ...inputSx, mb: 2.5 }}
            />

            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'rgba(255,255,255,0.75)', fontFamily: "'Inter', sans-serif", mb: 0.75 }}>
              Password
            </Typography>
            <TextField
              required fullWidth size="small"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setShowPassword(p => !p)}
                      edge="end"
                      sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'rgba(255,255,255,0.7)' } }}
                    >
                      {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ ...inputSx, mb: 3 }}
            />

            <Button
              type="submit" fullWidth variant="contained"
              disabled={loading}
              sx={{
                py: 1.3, mb: 2.5,
                fontSize: '0.9375rem', fontWeight: 600,
                fontFamily: "'Inter', sans-serif",
                borderRadius: '10px',
                textTransform: 'none',
                background: 'linear-gradient(135deg, #0D6EFD 0%, #0A58CA 100%)',
                boxShadow: '0 4px 16px rgba(13,110,253,0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0A58CA 0%, #083FA0 100%)',
                  boxShadow: '0 4px 20px rgba(13,110,253,0.55)',
                },
                '&:disabled': { opacity: 0.6 },
              }}
            >
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>

            <Typography sx={{ textAlign: 'center', fontSize: '0.875rem', color: 'rgba(255,255,255,0.45)', fontFamily: "'Inter', sans-serif" }}>
              Don't have an account?{' '}
              <Link to="/register" style={{ color: '#00AEEF', textDecoration: 'none', fontWeight: 600 }}>
                Sign up
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default Login;
